import { ReactElement, ReactNode } from 'react';
import { Succeeded, Failed } from '@/components/general/confirmation/Confirmation/Confirmation';
import { useTimeout } from '@/hooks/general/useTimeout';
import { CompletionStatus } from '@/types/enums/completionStatus';
import { Box } from '@mui/material';

const useHideCompletionStatusOnChange = (
    status: WithCompletionStatusProps['status'],
    resetStatus?: () => void,
    onlyResetStatusOnFail?: boolean,
) => {
    const HIDE_STATUS_AFTER_MS = 1500;

    useTimeout(
        () => {
            if (!onlyResetStatusOnFail || status === CompletionStatus.FAILED) resetStatus?.();
        },
        HIDE_STATUS_AFTER_MS,
        status,
    );
};

type StatusProps = Pick<WithCompletionStatusProps, 'status' | 'successMessage' | 'failureMessage'>;
const Status = (props: StatusProps): ReactElement => {
    if (props.status === CompletionStatus.SUCCEEDED)
        return <Succeeded message={props.successMessage} />;
    if (props.status === CompletionStatus.FAILED) return <Failed message={props.failureMessage} />;
    return <></>;
};

interface WithCompletionStatusProps {
    children: ReactNode;
    status: CompletionStatus;
    successMessage?: number;
    failureMessage?: number;
    resetStatus?: () => void;
    onlyResetStatusOnFail?: boolean;
    alignment?: 'flex-start' | 'flex-end' | 'center';
}

export const WithCompletionStatus = (props: WithCompletionStatusProps): ReactElement => {
    useHideCompletionStatusOnChange(props.status, props.resetStatus, props.onlyResetStatusOnFail);

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: props.alignment ?? 'center',
                alignItems: 'center',
                height: '2.6em',
            }}
        >
            {props.status === 'None' ? (
                props.children
            ) : (
                <Status
                    status={props.status}
                    successMessage={props.successMessage}
                    failureMessage={props.failureMessage}
                />
            )}
        </Box>
    );
};
